import * as t from 'io-ts';

import {ConversationPrecogId} from '../../store/data/precogs/conversationPrecogsActions';
import {makeApiType, makeEnumRuntimeType, RLazyTeammateIdRef} from './apiTypes';

/*
 * Runtime type.
 */

export enum PrecogTypesEnum {
  COMMENT = 'comment',
  MESSAGE = 'message',
}

export enum PrecogStatusTypesEnum {
  TYPING,
  SENT,
}

const RBasePrecog = t.type({
  teammateId: RLazyTeammateIdRef,
  conversationId: t.Integer,
  updatedAt: t.Integer,
  type: makeEnumRuntimeType(PrecogTypesEnum),
});

const RSentPrecog = t.intersection([
  RBasePrecog,
  t.type({
    status: t.literal(PrecogStatusTypesEnum.SENT),
    activityId: t.Integer,
  }),
]);

const RTypingPrecog = t.intersection([
  RBasePrecog,
  t.type({
    status: t.literal(PrecogStatusTypesEnum.TYPING),
  }),
]);

const RPrecogDirty = t.readonly(t.taggedUnion('status', [RSentPrecog, RTypingPrecog]), 'message');

const RPrecogContinueResultDirty = t.readonly(
  t.type({conflictLink: t.union([t.string, t.undefined])}),
  'precog_continue_result',
);

/*
 * Type.
 */

export interface TypingPrecogModel extends t.TypeOf<typeof RTypingPrecog> {}
export interface SentPrecogModel extends t.TypeOf<typeof RSentPrecog> {}

export type PrecogModel = TypingPrecogModel | SentPrecogModel;
export const RPrecog = t.clean<PrecogModel>(RPrecogDirty as any);

export interface PrecogContinueResultModel extends t.TypeOf<typeof RPrecogContinueResultDirty> {}
export const RPrecogContinueResult = t.clean<PrecogContinueResultModel>(RPrecogContinueResultDirty as any);

/*
 * IO methods.
 */

export function parsePrecog(src: any): TypingPrecogModel {
  const {precog} = src;
  return {
    teammateId: src.id,
    conversationId: precog && precog.conversation_id,
    updatedAt: precog && precog.updated_at,
    type: precog && precog.type,
    status: PrecogStatusTypesEnum.TYPING,
  };
}

export function parseConversationPrecogId(src: any): ConversationPrecogId {
  return {
    teammateId: src.teammate_id,
    conversationId: src.conversation_id,
  };
}

export function parseConversationPrecog(conversationId: number, src: any): TypingPrecogModel {
  return {
    teammateId: src.teammate_id,
    type: src.type,
    updatedAt: src.updated_at,
    conversationId,
    status: PrecogStatusTypesEnum.TYPING,
  };
}

export function parseSentPrecog(src: any): SentPrecogModel {
  const {precog} = src;
  return {
    teammateId: src.id,
    conversationId: precog && precog.conversation_id,
    updatedAt: precog && precog.updated_at,
    type: precog && precog.type,
    activityId: precog && precog.activity_id,
    status: PrecogStatusTypesEnum.SENT,
  };
}

export function parseConversationSentPrecog(conversationId: number, src: any): SentPrecogModel {
  return {
    teammateId: src.teammate_id,
    type: src.type,
    updatedAt: src.updated_at,
    conversationId,
    activityId: src.activity_id,
    status: PrecogStatusTypesEnum.SENT,
  };
}

export function parsePrecogContinueResult(src: any): PrecogContinueResultModel {
  return {
    conflictLink: src && src.conflict_link,
  };
}

/*
 * API type.
 */

export const ApiPrecogModel = makeApiType('precog', RPrecog, parsePrecog);
export const ApiPrecogContinueResultModel = makeApiType(
  'precog_continue_result',
  RPrecogContinueResult,
  parsePrecogContinueResult,
);
